import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Send, Loader2, Gift, BookOpen, Users } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USAGE_TYPES = [
  "Bireysel Kullanıcı",
  "İşveren / İşyeri",
  "Belediye / Kamu Kurumu",
  "Sağlık / Eğitim Kurumu",
  "Eczane / Medikal Firma",
  "Diğer",
];

const LeadForm = ({ calculationData, result, onSubmitted, onBack }) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [usageType, setUsageType] = useState("");
  const [otherUsage, setOtherUsage] = useState("");
  const [kvkkConsent, setKvkkConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [legalUrls, setLegalUrls] = useState({
    kvkk_url: "https://alcodot.com/kvkk"
  });

  useEffect(() => {
    fetchDiscount();
    fetchLegalUrls();
  }, []);

  const fetchDiscount = async () => {
    try {
      const response = await axios.get(`${API}/discount`);
      setDiscount(response.data);
    } catch (error) {
      console.error("Discount error:", error);
    }
  };

  const fetchLegalUrls = async () => {
    try {
      const response = await axios.get(`${API}/legal-settings`);
      if (response.data) {
        setLegalUrls(response.data);
      }
    } catch (error) {
      console.error("Legal URLs error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Lütfen adınızı girin");
      return;
    }

    if (!email.trim() || !email.includes("@")) {
      toast.error("Lütfen geçerli bir e-posta adresi girin");
      return;
    }

    if (!phone.trim() || phone.length < 10) {
      toast.error("Lütfen geçerli bir telefon numarası girin");
      return;
    }

    if (!usageType) {
      toast.error("Lütfen kullanım tipi seçin");
      return;
    }

    if (usageType === "Diğer" && !otherUsage.trim()) {
      toast.error("Lütfen diğer kullanım tipini belirtin");
      return;
    }

    if (!kvkkConsent) {
      toast.error("KVKK onayı gereklidir");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        lead_data: {
          name,
          email,
          phone,
          usage_type: usageType,
          other_usage: usageType === "Diğer" ? otherUsage : null,
          kvkk_consent: kvkkConsent,
          marketing_consent: marketingConsent,
          sms_consent: smsConsent,
        },
        calculation_data: calculationData,
      };

      const response = await axios.post(`${API}/calculate`, payload);

      if (response.data) {
        toast.success("Tebrikler! Bilgileriniz kaydedildi.");
        setTimeout(() => {
          navigate('/thank-you');
        }, 1000);
      }
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error(
        "Bir hata oluştu. Lütfen tekrar deneyin: " +
          (error.response?.data?.detail || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Value Proposition Header */}
      <div className="glass-card-premium rounded-3xl p-8 neon-border mb-6 text-center">
        <h3 className="text-3xl font-black text-gradient mb-6">Bilgilerinizi Kaydedin, Kazanın!</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* E-Book */}
          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-5 rounded-2xl border-2 border-blue-500/30">
            <BookOpen className="w-10 h-10 text-blue-400 mx-auto mb-3" />
            <p className="text-white font-bold text-sm mb-1">ÜCRETSIZ</p>
            <p className="text-gray-300 text-xs">10 Adımda Güvenli Sürüş Rehberi (PDF)</p>
          </div>

          {/* Discount */}
          {discount && discount.is_active && (
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 p-5 rounded-2xl border-2 border-green-500/30">
              <Gift className="w-10 h-10 text-green-400 mx-auto mb-3" />
              <p className="text-white font-bold text-sm mb-1">{discount.discount_text}</p>
              <p className="text-gray-300 text-xs">İlk Alışverişinizde</p>
            </div>
          )}

          {/* VIP */}
          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 p-5 rounded-2xl border-2 border-purple-500/30">
            <Users className="w-10 h-10 text-purple-400 mx-auto mb-3" />
            <p className="text-white font-bold text-sm mb-1">VIP GRUP</p>
            <p className="text-gray-300 text-xs">Özel Fırsatlara Erken Erişim</p>
          </div>
        </div>
      </div>

      <div className="glass-card-premium rounded-3xl p-8 sm:p-10 neon-border hover-lift">
        <div className="flex items-center mb-8">
          <Button
            onClick={onBack}
            variant="ghost"
            size="sm"
            className="mr-4 text-teal-400 hover:bg-teal-500/10 hover:text-teal-300 rounded-xl"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h3 className="text-3xl font-black text-gradient" data-testid="lead-form-title">
            İletişim Bilgileri
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div className="space-y-3">
            <Label htmlFor="name" className="text-sm font-bold text-teal-400 uppercase tracking-wider">
              Ad Soyad *
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Ad Soyad"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 focus:border-teal-500 text-white text-lg placeholder:text-gray-500"
              data-testid="name-input"
            />
          </div>

          {/* Email */}
          <div className="space-y-3">
            <Label htmlFor="email" className="text-sm font-bold text-teal-400 uppercase tracking-wider">
              E-posta *
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="ornek@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 focus:border-teal-500 text-white text-lg placeholder:text-gray-500"
              data-testid="email-input"
            />
          </div>

          {/* Phone */}
          <div className="space-y-3">
            <Label htmlFor="phone" className="text-sm font-bold text-teal-400 uppercase tracking-wider">
              Telefon *
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder="05XX XXX XX XX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 focus:border-teal-500 text-white text-lg placeholder:text-gray-500"
              data-testid="phone-input"
            />
          </div>

          {/* Usage Type */}
          <div className="space-y-3">
            <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
              Kullanım Tipi *
            </Label>
            <Select value={usageType} onValueChange={setUsageType}>
              <SelectTrigger className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white" data-testid="usage-type-select">
                <SelectValue placeholder="Seçiniz" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-teal-500/30">
                {USAGE_TYPES.map((type) => (
                  <SelectItem key={type} value={type} className="text-white hover:bg-teal-500/20">
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Other Usage */}
          {usageType === "Diğer" && (
            <div className="space-y-3">
              <Label
                htmlFor="otherUsage"
                className="text-sm font-bold text-teal-400 uppercase tracking-wider"
              >
                Lütfen Belirtin *
              </Label>
              <Input
                id="otherUsage"
                type="text"
                placeholder="Kullanım amacınızı belirtin"
                value={otherUsage}
                onChange={(e) => setOtherUsage(e.target.value)}
                className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 focus:border-teal-500 text-white text-lg placeholder:text-gray-500"
                data-testid="other-usage-input"
              />
            </div>
          )}

          {/* Consents */}
          <div className="space-y-4 bg-gray-900/30 p-6 rounded-2xl border-2 border-teal-500/20">
            <div className="flex items-start space-x-4">
              <input
                type="checkbox"
                id="kvkk"
                checked={kvkkConsent}
                onChange={(e) => setKvkkConsent(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-teal-500/50 bg-gray-900/50 checked:bg-teal-500 checked:border-teal-500 cursor-pointer"
                data-testid="kvkk-checkbox"
              />
              <div className="text-sm text-gray-300 leading-relaxed">
                <span className="font-bold text-teal-400">KVKK Aydınlatma Metni *</span>
                <br />
                Kişisel verilerimin işlenmesine ilişkin{" "}
                <a 
                  href={legalUrls.kvkk_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-teal-400 underline hover:text-teal-300"
                  onClick={(e) => e.stopPropagation()}
                >
                  aydınlatma metnini
                </a>{" "}
                okudum ve anladım. Verilerimin işlenmesini kabul ediyorum.
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <input
                type="checkbox"
                id="marketing"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-teal-500/50 bg-gray-900/50 checked:bg-teal-500 checked:border-teal-500 cursor-pointer"
                data-testid="marketing-checkbox"
              />
              <label
                htmlFor="marketing"
                className="text-sm text-gray-300 cursor-pointer leading-relaxed"
              >
                <span className="font-semibold text-white">E-posta ile İletişim İzni</span>
                <br />
                Kampanyalar, yeni ürünler ve özel teklifler hakkında e-posta almak istiyorum.
              </label>
            </div>

            <div className="flex items-start space-x-4">
              <input
                type="checkbox"
                id="sms"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-teal-500/50 bg-gray-900/50 checked:bg-teal-500 checked:border-teal-500 cursor-pointer"
                data-testid="sms-checkbox"
              />
              <label
                htmlFor="sms"
                className="text-sm text-gray-300 cursor-pointer leading-relaxed"
              >
                <span className="font-semibold text-white">SMS ile İletişim İzni</span>
                <br />
                Kampanya ve özel fırsatlar hakkında SMS almak istiyorum.
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-16 rounded-2xl bg-gradient-to-r from-teal-500 via-cyan-500 to-sky-500 hover:from-teal-400 hover:via-cyan-400 hover:to-sky-400 text-black font-black text-xl shadow-lg neon-glow smooth-transition shimmer"
            data-testid="submit-lead-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Send className="w-6 h-6 mr-3" />
                Kaydet ve Hediyeni Al
              </>
            )}
          </Button>

          <p className="text-center text-xs text-gray-500 mt-4">
            ✓ Güvenli veri işleme · ✓ İstediğiniz zaman iptal edebilirsiniz
          </p>
        </form>
      </div>
    </div>
  );
};

export default LeadForm;